// @flow

import gql from 'graphql-tag';
import { get, omit, omitBy } from 'lodash';

import {
  type ApolloInjection,
  type PaymentSource,
  type PaymentSourceMethod,
  type SupportedPaymentMethod,
  paymentSourceMethods,
} from '@zapier/payment/src/types';
import {
  useMutation as defaultUseMutation,
  useQuery as defaultUseQuery,
} from '@apollo/react-hooks';

type Input = {
  ...$Exact<ApolloInjection>,
  accountId: number,
  method: ?PaymentSourceMethod,
  onError: (error: Error, method: PaymentSourceMethod) => void,
  onSuccess: ({ saveBankPaymentSource: PaymentSource }) => void,
};

type Output = {
  error: ?string,
  isLoading: boolean,
  isSaving: boolean,
  onSave: ({}, PaymentSourceMethod) => void,
  paymentSources: Array<PaymentSource>,
  primarySource: ?PaymentSource,
  supportedPaymentMethods: Array<SupportedPaymentMethod>,
};

const paymentSourceFragment = gql`
  fragment paymentSource on PaymentSource {
    details {
      ... on PaymentSourceBank {
        accountHolderName
        accountType
        addressCity
        addressCountry
        addressState
        addressZip
        bankName
        last4
        ownershipType
        routingNumber
        verified
      }

      ... on PaymentSourceCard {
        addressCity
        addressCountry
        addressState
        addressZip
        brand
        expiry
        last4
        name
      }

      ... on PaymentSourcePaypal {
        email
      }
    }
    id
    method
  }
`;

const paymentMethodsQuery = gql`
  ${paymentSourceFragment}

  query paymentMethodsQuery($id: Int!) {
    account(id: $id) {
      id
      supportedPaymentMethodsv2 {
        paymentProcessor
        method
      }
      paymentSources {
        id
        ...paymentSource
      }
    }
  }
`;

const saveMutation = {
  bank: gql`
    ${paymentSourceFragment}

    mutation SaveBankPaymentSource($accountId: Int!, $data: BankInput!) {
      saveBankPaymentSource(accountId: $accountId, data: $data) {
        ...paymentSource
      }
    }
  `,
  card: gql`
    ${paymentSourceFragment}

    mutation SaveCardPaymentSource($accountId: Int!, $data: CardInput!) {
      saveCardPaymentSource(accountId: $accountId, data: $data) {
        ...paymentSource
      }
    }
  `,
  paypal: gql`
    ${paymentSourceFragment}

    mutation SavePaypalPaymentSource($accountId: Int!, $data: PaypalInput!) {
      savePaypalPaymentSource(accountId: $accountId, data: $data) {
        ...paymentSource
      }
    }
  `,
};

const transformSaveDetails = (data: {}, method: PaymentSourceMethod) => {
  if (method === paymentSourceMethods.card) {
    return {
      ...data,
      // Removes all null properties from data these are returned
      // by the tokenization process and can be ignored, otherwise
      // graphql complains / also ignores metadata
      details: omit(
        omitBy(
          get(data, 'details', {}),
          val => val === null || val === undefined || val === ''
        ),
        ['metadata']
      ),
    };
  }

  return data;
};

const usePaymentSources = ({
  accountId,
  method,
  onError,
  onSuccess,
  ...props
}: Input): Output => {
  const useQuery = props.useQuery || defaultUseQuery;
  const useMutation = props.useMutation || defaultUseMutation;

  const queryResults = useQuery(paymentMethodsQuery, {
    skip: !accountId,
    variables: {
      id: accountId,
    },
  });

  const [save, saveResults] = useMutation(
    method ? saveMutation[method] : saveMutation.card,
    {
      awaitRefetchQueries: true,
      // $FlowFixMe - method cannot be null in this situation
      onError: error => onError(error, method),
      onCompleted: onSuccess,
      refetchQueries: () => [
        {
          query: paymentMethodsQuery,
          variables: {
            id: accountId,
          },
        },
      ],
    }
  );

  const paymentSources = get(
    queryResults,
    ['data', 'account', 'paymentSources'],
    []
  );

  const onSave = (data: {}, selectedMethod: PaymentSourceMethod) => {
    save({
      variables: {
        accountId,
        data: transformSaveDetails(data, selectedMethod),
      },
    });
  };

  const error = queryResults.error
    ? 'There was an issue loading your payment sources. Please try again'
    : saveResults.error;

  return {
    error,
    isLoading: queryResults.loading,
    isSaving: saveResults.loading,
    onSave,
    paymentSources,
    primarySource: paymentSources[0],
    supportedPaymentMethods: get(
      queryResults,
      ['data', 'account', 'supportedPaymentMethodsv2'],
      []
    ),
  };
};

export default usePaymentSources;
